import { Controller } from 'stimulus'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import '@geoman-io/leaflet-geoman-free'
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css'
import { createBaseLayers } from './shared/leaflet_map'

const CORAL  = '#D97757' // non affecté (à rattacher à un portefeuille)
const SLATE  = '#94a3b8' // déjà en portefeuille
const BLUE   = '#2563eb' // sélectionné (zone dessinée)

// Territory map: markers are fetched per visible bbox from #map_data — grid clusters when
// zoomed out, site pins when zoomed in, so the whole DB is never loaded at once.
export default class extends Controller {
  static targets = [
    'map', 'count', 'selectionPanel', 'clientIds', 'clientIds2', 'zoneGeojson',
    'categoryFilter', 'portfolioFilter', 'stats',
    'assignModal', 'assignClientName', 'assignClientId', 'assignPortfolio', 'assignError',
  ]

  connect() {
    this.selectedIds = []
    this.selectedSet = new Set()
    this.zoneGeo = null
    this.category = ''
    this.portfolio = ''
    this._seq = 0
    this._globalStats = this._json('stats') || {}
    this._canAssign = this.hasAssignPortfolioTarget && this.assignPortfolioTarget.options.length > 0
    this._whenSized(() => { if (!this.map) { this._buildMap(); this._initView() } })
  }

  disconnect() {
    if (this._ro) this._ro.disconnect()
    if (this.map) { this.map.remove(); this.map = null }
  }

  _whenSized(cb, tries = 0) {
    const t = this.mapTarget
    if (t && t.offsetWidth > 50 && t.offsetHeight > 50) return cb()
    if (tries > 60) return cb()
    requestAnimationFrame(() => this._whenSized(cb, tries + 1))
  }

  _buildMap() {
    this.map = L.map(this.mapTarget, { zoomControl: true, preferCanvas: false }).setView([46.6, 2.5], 6)

    const { osm, plan, satellite, adminLimits } = createBaseLayers()
    osm.addTo(this.map) // land directly on OpenStreetMap
    L.control.layers({ OpenStreetMap: osm, [I18n.t('crm.portfolio.map_layer_plan')]: plan,
                       [I18n.t('crm.portfolio.map_layer_satellite')]: satellite },
      { [I18n.t('crm.portfolio.map_admin_limits')]: adminLimits }, { collapsed: true, position: 'topright' }).addTo(this.map)

    this.layer = L.layerGroup().addTo(this.map) // markers, rebuilt on every viewport load

    if (typeof ResizeObserver !== 'undefined') {
      this._ro = new ResizeObserver(() => this.map && this.map.invalidateSize())
      this._ro.observe(this.mapTarget)
    }
    setTimeout(() => this.map.invalidateSize(), 300)

    this.map.pm.addControls({
      position: 'topright', drawCircle: false, drawCircleMarker: false, drawMarker: false,
      drawPolyline: false, drawText: false, cutPolygon: false, rotateMode: false, dragMode: false,
      editMode: true, removalMode: true,
    })
    this.map.on('pm:create', (e) => this._onZoneDrawn(e.layer))
    this.map.on('pm:remove', () => this._clearZoneState())
    this.map.on('moveend', () => this._scheduleLoad())

    // "Affecter" buttons live inside dynamically-created Leaflet popups → delegate.
    this.mapTarget.addEventListener('click', (e) => {
      const b = e.target.closest('[data-assign-id]')
      if (!b) return
      e.preventDefault()
      this._openAssign(b.getAttribute('data-assign-id'), b.getAttribute('data-assign-name'))
    })
  }

  _initView() {
    this._addRecenterControl()
    this._fitAndLoad(this._json('bounds'), 13)
  }

  _scheduleLoad() { clearTimeout(this._t); this._t = setTimeout(() => this._load(), 300) }

  // fitBounds does NOT emit `moveend` when the target view ≈ the current one, so the
  // moveend→_load chain can silently skip a reload (e.g. filtering to a nearby zone).
  // Fit synchronously and load explicitly, cancelling the redundant debounced load.
  _fitAndLoad(b, maxZoom = 13, padding = 40) {
    if (!b || b.sw_lat == null) return this._load()
    this.map.fitBounds([[b.sw_lat, b.sw_lng], [b.ne_lat, b.ne_lng]], { padding: [padding, padding], maxZoom, animate: false })
    clearTimeout(this._t)
    this._load()
  }

  _load() {
    if (!this.map) return
    const bb = this.map.getBounds()
    const url = new URL(this.data.get('url'), window.location.origin)
    url.searchParams.set('sw_lat', bb.getSouth()); url.searchParams.set('sw_lng', bb.getWest())
    url.searchParams.set('ne_lat', bb.getNorth()); url.searchParams.set('ne_lng', bb.getEast())
    url.searchParams.set('z', this.map.getZoom())
    if (this.category) url.searchParams.set('category_id', this.category)
    if (this.portfolio) url.searchParams.set('portfolio_id', this.portfolio)
    const seq = ++this._seq
    fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then((r) => r.json())
      .then((d) => { if (seq === this._seq) { this._lastData = d; this._render(d) } })
      .catch(() => {})
  }

  _render(d) {
    if (!this.layer || !d) return
    this.layer.clearLayers()
    if (d.mode === 'clusters') {
      ;(d.clusters || []).forEach((c) => this.layer.addLayer(this._clusterMarker(c)))
    } else {
      ;(d.points || []).forEach((p) => this.layer.addLayer(this._pointMarker(p)))
    }
    this._updateStats(d)
  }

  _clusterMarker(c) {
    const color = c.unassigned > 0 ? CORAL : SLATE
    const s = c.count < 10 ? 34 : c.count < 100 ? 40 : 48
    const html = `<div style="width:${s}px;height:${s}px;line-height:${s}px;border-radius:9999px;background:${color};
      color:#fff;font-weight:700;font-size:13px;text-align:center;border:2px solid #fff;
      box-shadow:0 0 0 5px ${color}2e,0 2px 6px rgba(15,23,42,.35)">${c.count}</div>`
    const m = L.marker([c.lat, c.lng], { icon: L.divIcon({ html, className: '', iconSize: [s, s] }) })
    m.on('click', () => this.map.flyTo([c.lat, c.lng], Math.min((this.map.getZoom() || 6) + 3, 15)))
    return m
  }

  _pointMarker(p) {
    const color = this.selectedSet.has(p.id) ? BLUE : (p.portfolio_id ? SLATE : CORAL)
    const m = L.marker([p.lat, p.lng], {
      icon: L.divIcon({ html: this._pinSvg(color), className: '', iconSize: [28, 36], iconAnchor: [14, 34], popupAnchor: [0, -32] }),
    })
    m.bindTooltip(p.site && p.site !== p.name ? `${p.name} — ${p.site}` : p.name, { direction: 'top', offset: [0, -30] })
    m.bindPopup(this._sitePopup(p))
    return m
  }

  _pinSvg(color) {
    return `<svg width="28" height="36" viewBox="0 0 28 36" xmlns="http://www.w3.org/2000/svg">
      <path d="M14 0C6.3 0 0 6.3 0 14c0 9.8 14 22 14 22s14-12.2 14-22C28 6.3 21.7 0 14 0z" fill="${color}" stroke="#fff" stroke-width="2.5"/>
      <circle cx="14" cy="14" r="5" fill="#fff"/></svg>`
  }

  _sitePopup(p) {
    const site = p.site && p.site !== p.name ? `<div style="color:#64748b;font-size:12px;margin-top:1px">${this._esc(p.site)}</div>` : ''
    const name = this._esc(p.name)
    let body
    if (p.portfolio_id) {
      body = `<div style="display:inline-flex;align-items:center;gap:6px;color:#475569">
        <span style="width:8px;height:8px;border-radius:9999px;background:${SLATE};flex:none"></span>
        <a href="/crm/portfolios/${p.portfolio_id}/edit" target="_blank" rel="noopener" style="color:#2563eb;font-weight:600">${this._esc(p.portfolio_name || I18n.t('crm.portfolio.map_portfolio_label'))}&nbsp;↗</a>
      </div>`
    } else {
      const badge = `<div style="display:inline-flex;align-items:center;gap:6px;color:#475569">
        <span style="width:8px;height:8px;border-radius:9999px;background:${CORAL};flex:none"></span> ${I18n.t('crm.portfolio.legend_unassigned')}</div>`
      const btn = this._canAssign
        ? `<button type="button" data-assign-id="${p.id}" data-assign-name="${name}"
             style="display:inline-flex;align-items:center;gap:6px;padding:6px 11px;border:none;border-radius:8px;
                    background:${CORAL};color:#fff;font-weight:600;font-size:12px;cursor:pointer;white-space:nowrap">
             <span style="font-size:14px;line-height:1">＋</span> ${I18n.t('crm.portfolio.assign_client_title')}</button>`
        : ''
      body = `<div style="display:flex;flex-direction:column;align-items:flex-start;gap:8px">${badge}${btn}</div>`
    }
    return `<div style="min-width:190px;font-size:13px">
      <div style="font-weight:700;color:#0f172a">${name}</div>${site}
      <div style="margin:8px 0 6px">${body}</div>
      <a href="/clients/${p.id}" target="_blank" rel="noopener" style="color:#64748b;font-weight:500;font-size:12px">${I18n.t('crm.portfolio.popup_open_client')} →</a></div>`
  }

  onCategoryChange() {
    this.category = this.hasCategoryFilterTarget ? this.categoryFilterTarget.value : ''
    this._fitFiltered()
  }

  onPortfolioChange() {
    this.portfolio = this.hasPortfolioFilterTarget ? this.portfolioFilterTarget.value : ''
    this._fitFiltered()
  }

  // The server computes the filter's bbox; moveend then reloads the viewport's markers.
  _fitFiltered() {
    if (!this.category && !this.portfolio) return this._fitAndLoad(this._json('bounds'), 13)

    const url = new URL(this.data.get('url'), window.location.origin)
    url.searchParams.set('fit', '1')
    if (this.category) url.searchParams.set('category_id', this.category)
    if (this.portfolio) url.searchParams.set('portfolio_id', this.portfolio)
    fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then((r) => r.json())
      .then((d) => this._fitAndLoad(d.bounds, 15, 50))
      .catch(() => this._load())
  }

  startDraw() { if (this.map) this.map.pm.enableDraw('Polygon') }

  clearZone() {
    if (!this.map) return
    try { this.map.pm.disableDraw() } catch (e) {}
    try { this.map.pm.getGeomanLayers().forEach((l) => this.map.removeLayer(l)) } catch (e) {}
    this._clearZoneState()
  }

  _onZoneDrawn(layer) {
    try { this.map.pm.getGeomanLayers().forEach((l) => { if (l !== layer) this.map.removeLayer(l) }) } catch (e) {}
    this.zoneLayer = layer
    this.zoneGeo = layer.toGeoJSON().geometry
    layer.on('pm:edit', () => { this.zoneGeo = layer.toGeoJSON().geometry; this._fetchZone() })
    this._fetchZone()
  }

  _fetchZone() {
    if (!this.zoneGeo) return
    const body = new URLSearchParams({ geojson: JSON.stringify(this.zoneGeo) })
    if (this.category) body.set('category_id', this.category)
    if (this.portfolio) body.set('portfolio_id', this.portfolio)
    fetch(this.data.get('zoneUrl'), {
      method: 'POST', credentials: 'same-origin', body,
      headers: { 'Content-Type': 'application/x-www-form-urlencoded', Accept: 'application/json', 'X-CSRF-Token': this._csrf() },
    })
      .then((r) => r.json())
      .then((d) => {
        this.selectedIds = d.client_ids || []
        this.selectedSet = new Set(this.selectedIds)
        this._refreshSelectionUI(d.count || 0)
        this._render(this._lastData) // re-colour visible pins that fall in the zone
      })
      .catch(() => {})
  }

  _clearZoneState() {
    this.zoneLayer = null
    this.zoneGeo = null
    this.selectedIds = []
    this.selectedSet = new Set()
    this._refreshSelectionUI(0)
    this._render(this._lastData)
  }

  _refreshSelectionUI(count) {
    if (this.hasCountTarget) this.countTarget.textContent = count
    const ids = this.selectedIds.join(',')
    if (this.hasClientIdsTarget) this.clientIdsTarget.value = ids
    if (this.hasClientIds2Target) this.clientIds2Target.value = ids
    if (this.hasZoneGeojsonTarget) this.zoneGeojsonTarget.value = this.zoneGeo ? JSON.stringify(this.zoneGeo) : ''
    if (this.hasSelectionPanelTarget) this.selectionPanelTarget.classList.toggle('hidden', count === 0)
  }

  // Colour-coded to match the pins: coral = unassigned, grey = in a book, blue = in the view.
  _updateStats(d) {
    if (!this.hasStatsTarget) return
    const g = this._globalStats || {}
    const n = (x) => (x == null ? '—' : Number(x).toLocaleString(I18n.locale))
    const chip = (val, label, color) =>
      `<span class="whitespace-nowrap"><strong style="color:${color || '#334155'}">${n(val)}</strong> <span class="text-slate-400">${label}</span></span>`
    const dot = '<span class="mx-2.5 text-slate-300">·</span>'
    const base = [
      chip(g.clients, I18n.t('crm.portfolio.stats_clients')),
      chip(g.assigned, I18n.t('crm.portfolio.stats_assigned'), '#64748b'),
      chip(g.unassigned, I18n.t('crm.portfolio.stats_unassigned'), CORAL),
    ].join(dot)
    const view = [chip(d ? d.total : null, I18n.t('crm.portfolio.stats_in_view'), BLUE)]
    if (g.out_of_zone) view.push(chip(g.out_of_zone, I18n.t('crm.portfolio.stats_out_of_zone'), '#d97706'))
    this.statsTarget.innerHTML = `${base}<span class="mx-3 text-slate-200">|</span>${view.join(dot)}`
  }

  _openAssign(id, name) {
    if (!this.hasAssignModalTarget) return
    if (this.hasAssignClientIdTarget) this.assignClientIdTarget.value = id
    if (this.hasAssignClientNameTarget) this.assignClientNameTarget.textContent = name || I18n.t('crm.portfolio.assign_client_placeholder')
    if (this.hasAssignErrorTarget) this.assignErrorTarget.classList.add('hidden')
    if (this.map) this.map.closePopup()
    this.assignModalTarget.classList.remove('hidden')
  }

  closeAssign() { if (this.hasAssignModalTarget) this.assignModalTarget.classList.add('hidden') }

  submitAssign() {
    const clientId = this.hasAssignClientIdTarget ? this.assignClientIdTarget.value : ''
    const pfId = this.hasAssignPortfolioTarget ? this.assignPortfolioTarget.value : ''
    if (!clientId || !pfId) return
    fetch(this.data.get('assign-url'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': this._csrf(), Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ client_id: clientId, portfolio_id: pfId }),
    })
      .then((r) => r.json().then((j) => ({ ok: r.ok, j })))
      .then(({ ok, j }) => {
        if (!ok || !j.ok) throw new Error(j.error || I18n.t('crm.portfolio.assign_failed'))
        if (this._globalStats.unassigned != null) this._globalStats.unassigned -= 1
        if (this._globalStats.assigned != null) this._globalStats.assigned += 1
        this.closeAssign()
        this._load() // repaint the viewport → the client's pin turns grey
      })
      .catch((e) => {
        if (this.hasAssignErrorTarget) { this.assignErrorTarget.textContent = e.message; this.assignErrorTarget.classList.remove('hidden') }
      })
  }

  _addRecenterControl() {
    if (this._recenter) return
    const Ctl = L.Control.extend({
      options: { position: 'topleft' },
      onAdd: () => {
        const btn = L.DomUtil.create('a', 'leaflet-bar')
        btn.href = '#'; btn.title = I18n.t('crm.portfolio.map_recenter'); btn.innerHTML = '⌖'
        Object.assign(btn.style, { display: 'flex', alignItems: 'center', justifyContent: 'center', width: '30px', height: '30px', fontSize: '18px', background: '#fff', color: '#334155', textDecoration: 'none' })
        L.DomEvent.on(btn, 'click', (e) => {
          L.DomEvent.stop(e)
          this.portfolio = ''; this.category = ''
          if (this.hasPortfolioFilterTarget) this.portfolioFilterTarget.value = ''
          if (this.hasCategoryFilterTarget) this.categoryFilterTarget.value = ''
          this._fitAndLoad(this._json('bounds'), 13)
        })
        return btn
      },
    })
    this._recenter = new Ctl()
    this.map.addControl(this._recenter)
  }

  _json(key) { try { return JSON.parse(this.data.get(key)) } catch (e) { return null } }
  _csrf() { return document.querySelector('meta[name="csrf-token"]')?.content || '' }
  _esc(s) { const d = document.createElement('div'); d.textContent = s == null ? '' : s; return d.innerHTML }
}
